import { Card } from '@citizen-walk/shared-ui';
import type { ReactNode } from 'react';
import { SignInPage } from './SignInPage';
import { useAdminAuth, type AdminAuthState } from './useAdminAuth';

/**
 * Admin app の認証ガード (Sprint admin 第 9 弾、Phase 5.4)。
 *
 * 未認証 → SignInPage 表示
 * 認証済 + role が master / district のいずれか → children 表示
 * 認証済 + role なし → 「権限なし」表示 (custom claim 未付与の通常 user が誤って admin app に来た場合)
 *
 * 旧 admin との差分:
 *   - 旧は OAuth 中心 (Google) + 一部 email/password、v2 minimum は email/password のみ
 *   - 旧は districtAdmin が複数地区に属する場合の selector を持つ、v2 minimum は単一地区想定
 */

export interface AdminAuthContext {
  user: AdminAuthState['user'];
  role: AdminAuthState['role'];
  accessibleDistricts: string[];
  /** district admin の担当地区配列 (master は空、admin 第 11 弾 posts moderation で利用)。 */
  districts: string[];
  signOut: () => Promise<void>;
}

export interface AdminAuthGateProps {
  children: (ctx: AdminAuthContext) => ReactNode;
}

export function AdminAuthGate({ children }: AdminAuthGateProps) {
  const { state, signOutAdmin } = useAdminAuth();

  if (!state.ready) {
    return (
      <div className="mx-auto max-w-md py-12">
        <Card padding="md">
          <p className="text-sm text-slate-500">認証状態を確認中…</p>
        </Card>
      </div>
    );
  }

  if (!state.user) {
    return <SignInPage />;
  }

  if (state.role === null) {
    return (
      <div className="mx-auto max-w-md py-12">
        <Card padding="lg">
          <h2 className="text-base font-bold text-slate-900 mb-2">権限がありません</h2>
          <p className="text-sm text-slate-700">
            このアカウント (<code>{state.user.email}</code>) には master / district admin
            の権限が付与されていません。
          </p>
          <p className="mt-3 text-xs text-slate-500">
            管理者権限の付与は master ロールの担当者にお問い合わせください。
          </p>
          <button
            type="button"
            onClick={() => void signOutAdmin()}
            className="mt-4 text-sm text-primary-600 hover:text-primary-700 hover:underline"
          >
            別のアカウントでサインイン
          </button>
        </Card>
      </div>
    );
  }

  return (
    <>
      {children({
        user: state.user,
        role: state.role,
        accessibleDistricts: state.accessibleDistricts,
        districts: state.districts,
        signOut: signOutAdmin,
      })}
    </>
  );
}
